import React from 'react';

import { useTranslation } from 'react-i18next';
import { MEMBERSHIP_URL, ONLINE_SALES_URL, PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from 'shared/constants';
import { useLangNavigate } from 'shared/hooks';
import { IframeDialog } from 'shared/ui';

type FooterNavLinkProps = {
  to: string;
  menu: string;
  section: string;
  children: React.ReactNode;
};

// 푸터 내부 링크. 진짜 <a href>(크롤 가능)이면서, 클릭 시엔 기존 SPA 이동 + GTM 이벤트를 그대로 유지한다.
const FooterNavLink = ({ to, menu, section, children }: FooterNavLinkProps) => {
  const { lang, navigateWithLang } = useLangNavigate();
  const href = lang ? `/${lang}${to}` : to;

  return (
    <a
      href={href}
      className="border-0 bg-transparent p-0 text-[#4F4F4F] hover:underline"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigateWithLang(to);
        window.dataLayer?.push({ event: 'click_footer', menu, section });
      }}
    >
      {children}
    </a>
  );
};

const Footer = () => {
  const { t } = useTranslation('common');
  const { lang } = useLangNavigate();

  return (
    <footer
      className="flex w-full shrink-0 items-center justify-center bg-[#F6F6F6] py-10 md:py-[65px]"
      id="footer-content"
    >
      <div data-nosnippet className="w-full max-w-[1440px] px-5 md:px-6">
        <div className="flex flex-wrap justify-between gap-10 text-c1 md:flex-nowrap">
          {/* md:flex-nowrap: 1413px 미만에서 칼럼이 2~3단으로 꺾이는 대신 한 줄 유지 + 칼럼 내부 텍스트만 줄바꿈.
              120px 갭은 1440px+ 전용 — 그 아래에서 120px 갭이면 칼럼 min-w 합 + 우측 링크 블록이
              최소 캔버스(1280px)를 넘어 가로 스크롤이 생김 */}
          <div className="hidden w-full flex-wrap gap-8 md:mb-20 md:flex md:flex-nowrap md:gap-[60px] min-[1440px]:gap-[120px]">
            <div className="min-w-[160px] md:min-w-[180px]">
              <h3 className="mb-2 w-full border-b border-[#4F4F4F] pb-2 text-[#4F4F4F]">{t('footer.intro.title')}</h3>
              <ul className="space-y-1 text-gray-30">
                <li>
                  <FooterNavLink to="/guide" menu="Booking Guide" section="Intro">
                    {t('footer.intro.bookingGuide')}
                  </FooterNavLink>
                </li>

                <li>
                  <FooterNavLink to="/about-us" menu="About CheckmateKorea" section="Intro">
                    {t('footer.intro.about')}
                  </FooterNavLink>
                </li>

                <li>
                  <FooterNavLink to="/korean-housing-info" menu="Korean Housing Info" section="Intro">
                    {t('footer.intro.koreanHousingInfo')}
                  </FooterNavLink>
                </li>
              </ul>
            </div>

            <div className="min-w-[260px] max-w-full lg:min-w-[500px] md:min-w-[420px]">
              <h3 className="mb-2 w-full border-b border-[#4F4F4F] pb-2 text-[#4F4F4F]">
                {t('footer.allServices.title')}
              </h3>
              <ul className="grid grid-cols-1 gap-y-1 text-gray-30 md:grid-cols-[1fr,minmax(0,120px)] md:gap-x-20 md:pr-5">
                <li>
                  <FooterNavLink
                    to="/listing?roomTypes=STUDIO%2CSHAREHOUSE%2COFFICETEL%2COTHERS"
                    menu="Room Search"
                    section="All Services"
                  >
                    {t('footer.allServices.roomSearch')}
                  </FooterNavLink>
                </li>

                <li>
                  {/* 내부 멤버십 화면은 아직 완성 전이라 고객에게 열지 않는다 — GNB 와 같은 노션 안내로 보낸다 */}
                  <a
                    href={MEMBERSHIP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4F4F4F] hover:underline"
                    onClick={() => {
                      window.dataLayer?.push({ event: 'click_footer', menu: 'Membership', section: 'All Services' });
                    }}
                  >
                    {t('footer.allServices.membership')}
                  </a>
                </li>

                <li>
                  <FooterNavLink
                    to="/request-room"
                    menu="Request a Room that meets your criteria"
                    section="All Services"
                  >
                    {t('footer.allServices.requestRoom')}
                  </FooterNavLink>
                </li>

                <li>
                  <FooterNavLink to="/checkmate-mall" menu="Checkmate Mall" section="All Services">
                    {t('footer.allServices.checkmateMall')}
                  </FooterNavLink>
                </li>

                <li>
                  <IframeDialog title="Check With You" src="https://tally.so/r/w5EgKZ">
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-[#4F4F4F] hover:underline"
                      onClick={() => {
                        window.dataLayer?.push({
                          event: 'click_footer',
                          menu: 'Check With You',
                          section: 'All Services',
                        });
                      }}
                    >
                      {t('footer.allServices.checkWithYou')}
                    </button>
                  </IframeDialog>
                </li>

                <li>
                  <FooterNavLink to="/jobs" menu="Jobs" section="All Services">
                    {t('footer.allServices.jobs')}
                  </FooterNavLink>
                </li>

                <li>
                  <IframeDialog title="Contract Signing Accompaniment" src="https://tally.so/r/mV4pR6">
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-[#4F4F4F] hover:underline"
                      onClick={() => {
                        window.dataLayer?.push({
                          event: 'click_footer',
                          menu: 'Contract Signing Accompaniment',
                          section: 'All Services',
                        });
                      }}
                    >
                      {t('footer.allServices.contractSigning')}
                    </button>
                  </IframeDialog>
                </li>

                <li></li>
              </ul>
            </div>

            <div className="min-w-[180px]">
              <h3 className="mb-2 w-full border-b border-[#4F4F4F] pb-2 text-[#4F4F4F]">
                {t('footer.findOutMore.title')}
              </h3>
              <ul className="space-y-1 text-gray-30">
                <li>
                  <FooterNavLink to="/reviews" menu="Reviews" section="Find Out More">
                    {t('footer.findOutMore.reviews')}
                  </FooterNavLink>
                </li>
                <li>
                  <a
                    href={lang ? `/${lang}/blog/` : '/blog/'}
                    className="border-0 bg-transparent p-0 text-[#4F4F4F] hover:underline"
                    onClick={() => {
                      window.dataLayer?.push({
                        event: 'click_footer',
                        menu: 'Blog',
                        section: 'Find Out More',
                      });
                    }}
                  >
                    {t('footer.findOutMore.blog')}
                  </a>
                </li>
                <li>
                  <a
                    href="https://buttercup-coral-e9a.notion.site/Help-Center-a65dec1c44ca44958a2d192d5119017e?pvs=4"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4F4F4F] hover:underline"
                    onClick={() => {
                      window.dataLayer?.push({
                        event: 'click_footer',
                        menu: 'Help Center',
                        section: 'Find Out More',
                      });
                    }}
                  >
                    {t('footer.findOutMore.helpCenter')}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex w-full flex-col items-start space-y-1 text-c4 text-gray-200 lg:ml-20 md:ml-10 md:w-auto md:items-end md:text-c1">
            <a
              href="https://admin.checkmatekorea.com/login"
              className="flex items-center gap-1 whitespace-nowrap text-[#4F4F4F] hover:underline"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_footer',
                  menu: '무료로 방 등록하기',
                  section: 'business',
                });
              }}
              target="_blank"
              rel="noreferrer"
            >
              무료로 방 등록하기 <span aria-hidden>↗</span>
            </a>
            <a
              href="https://tally.so/r/w8xaax"
              className="flex items-center gap-1 whitespace-nowrap text-[#4F4F4F] hover:underline"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_footer',
                  menu: '제휴협력 제안하기',
                  section: 'business',
                });
              }}
              target="_blank"
              rel="noreferrer"
            >
              제휴협력 제안하기 <span aria-hidden>↗</span>
            </a>
            <a
              href="https://housebuddy.oopy.io/"
              className="flex items-center gap-1 whitespace-nowrap text-[#4F4F4F] hover:underline"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_footer',
                  menu: '회사 소개',
                  section: 'business',
                });
              }}
              target="_blank"
              rel="noreferrer"
            >
              회사 소개 <span aria-hidden>↗</span>
            </a>
          </div>
        </div>

        <p className="mt-6 break-words text-c4 text-[#4F4F4F] md:mt-0">
          <span>상호명 (주)하우스버디 | 대표자 임지은 | 개인정보책임자 임지은 | 사업자 등록번호 586-87-03123 | </span>
          <a
            className="cursor-pointer text-[#4F4F4F] underline-offset-2 hover:underline"
            href={ONLINE_SALES_URL}
            target="_blank"
            rel="noreferrer"
          >
            통신판매업신고번호: 2024-서울동작-0725
          </a>
          <br />
          <span>서울특별시 동작구 현충로지하220 창업스튜디오 A-02호</span>
          <br />
          <a
            className="cursor-pointer text-[#4F4F4F] underline-offset-2 hover:underline"
            href={TERMS_OF_SERVICE_URL}
            target="_blank"
            rel="noreferrer"
          >
            이용약관
          </a>
          <span> | </span>
          <a
            className="cursor-pointer text-[#4F4F4F] underline-offset-2 hover:underline"
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noreferrer"
          >
            개인정보처리방침
          </a>
          <span> | 고객지원: official@checkmatekorea.com | 고객센터: 070-8080-4353</span>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
